import { FC, useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
	Alert,
	Box,
	Button,
	ButtonDropdown,
	Container,
	ContentLayout,
	Flashbar,
	Grid,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'

import HeaderWithStatusBadges from '../../../components/Common/HeaderWithStatusBadges'
import ApprovalDetails from '../../../components/Project/ApprovalDetails'
import EphemeralApprovalDetails from '../../../components/Project/EphemeralApprovalDetails'
import ApprovalForm from '../../../components/Project/ApprovalForm'
import EphemeralApprovalForm from '../../../components/Project/EphemeralApprovalForm'
import RejectionForm from '../../../components/Project/RejectionForm'
import LoadingSpinner from '../../../components/LoadingSpinner'
import CONSTANTS from '../../../constants'
import useSingleProject from '../../../hooks/query/useSingleProject'
import RequiredPermissions from '../../../helper/RequiredPermissions'
import CommentsOverview from '../../../components/Project/CommentsOverview'
import RenderProjectFields from './RenderProjectFields'
import { Project } from '../../../types/projects'
import useUserPermissions from '../../../hooks/query/useUserPermissions'
import ProjectResourcePanel from '../../../components/ProjectResource/ProjectResourcesPanel'
import ReadGenericTags from '../../../components/ControlledFormInputs/GenericTagsRead'
import EditGenericTags from '../../../components/ControlledFormInputs/GenericTagsEdit'
import EditGenericTagsEphemeral from '../../../components/ControlledFormInputs/GenericTagsEditEphemeral'
import ReadGenericTagsEphemeral from '../../../components/ControlledFormInputs/GenericTagsReadEphemeral'
import { useFlashbarStateStore } from '../../../stores/notifications/flashbarStateStore'
import AccessDetails from '../../../components/Project/AccessDetails'

const ProjectDetails: FC = () => {
	const CURRENT_USER_ID = JSON.parse(localStorage['okta-token-storage'])[
		'idToken'
	]['claims']['email']
	const { id } = useParams() as { id: string }
	const navigate = useNavigate()
	const location = useLocation()
	const { data: thisProject, isLoading } = useSingleProject(id, true)

	const thisProjectID = thisProject?.projectID || ''
	const isEphemeral =
		thisProject?.isephemeral !== undefined ? thisProject.isephemeral : false

	const {
		data: userPermissions
	} = useUserPermissions(CURRENT_USER_ID)
	const hasSpecificPermissions = (
		permissions: string[],
		requiredPermissions: string[]
	): boolean => {
		return requiredPermissions.some((requiredPerm) =>
			permissions.includes(requiredPerm)
		)
	}
	const specificPermissions = ['projects:*:*', 'projects:*:approve', '*:*:*']
	const hasRequiredPermissions =
		userPermissions &&
		hasSpecificPermissions(userPermissions.permissions, specificPermissions)
	const [showApprovalForm, setShowApprovalForm] = useState(false)
	const [showRejectionForm, setShowRejectionForm] = useState(false)

	const { items: flashbarItems, removeItem } = useFlashbarStateStore()

	useEffect(() => {
		return () => {
			flashbarItems.forEach(item => (item.id.split(':')[1] === `${location.pathname}ca/enroll`) ? console.log(item.id.split(':')[1]) : removeItem(item.id))
		}
	}, [location, flashbarItems, removeItem])

	if (!thisProject || isLoading) return <LoadingSpinner />

	if (thisProject.status === 'ERROR') {
		return (
			<Alert
				statusIconAriaLabel="Error"
				type="error"
				header="An error occurred"
			>
				{thisProject.details.message}
			</Alert>
		)
	}

	const projectVersionedObject = isEphemeral
		? getKeyValueEphmeralProject(thisProject)
		: getKeyValueProject(thisProject)

	const requestDetailFields = projectVersionedObject

	let isAllTagsApproved = false
	if (thisProject?.tags !== undefined) {
		isAllTagsApproved = true
		let accountTags = thisProject?.tags
		let tagsApprovalStatuses = accountTags.approvalStatus
		for (const tagStatus of tagsApprovalStatuses) {
			if (tagStatus.status !== 1) {
				isAllTagsApproved = false
			}
		}
	}

	const projectResources = [
		{ relativePath: 'sslCerts', description: 'Provision SSL Certificate' },
		{
			relativePath: 'ca/enroll',
			description: 'Configure Connectivity Analyzer'
		}
	]

	return (
		<ContentLayout
			header={
				<HeaderWithStatusBadges
					headerName={thisProject?.details?.projectName}
					status={thisProject?.status}
					approvalStatus={thisProject?.tags?.approvalStatus || []}
					navigateTo="/projects"
				/>
			}
		>
			<Grid
				gridDefinition={[
					{ colspan: { default: 3, xxs: 9 } },
					{ colspan: { default: 9, xxs: 3 } }
				]}
			>
				<SpaceBetween direction="vertical" size="l">
          {(flashbarItems.length > 0) && <Flashbar items={flashbarItems} />}
					<RenderProjectFields project={requestDetailFields} />
					{!hasRequiredPermissions &&
						thisProject.status === 'PENDING_APPROVAL' && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.PROJECT_REQUESTOR}
							>
								{!isEphemeral ? (
									<Container
										header={<Header variant="h3">Tags Details</Header>}
									>
										{thisProject?.tagsID && (
											<ReadGenericTags tagsId={thisProject.tagsID} layout={3} />
										)}
									</Container>
								) : (
									<Container>
										{thisProject?.tagsID && (
											<ReadGenericTagsEphemeral
												tagsId={thisProject.tagsID}
												layout={3}
											/>
										)}
									</Container>
								)}
							</RequiredPermissions>
						)}

					{(thisProject.status === 'APPROVED' ||
						thisProject.status === 'REJECTED') && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.PROJECT_REQUESTOR}
						>
							{!isEphemeral ? (
								<Container header={<Header variant="h3">Tags Details</Header>}>
									{thisProject?.tagsID && (
										<ReadGenericTags tagsId={thisProject.tagsID} layout={3} />
									)}
								</Container>
							) : (
								<Container>
									{thisProject?.tagsID && (
										<ReadGenericTagsEphemeral
											tagsId={thisProject.tagsID}
											layout={3}
										/>
									)}
								</Container>
							)}
						</RequiredPermissions>
					)}
					{thisProject.status === 'PENDING_APPROVAL' &&
						hasRequiredPermissions && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.PROJECT_APPROVER}
							>
								{!isEphemeral ? (
									<Container
										header={<Header variant="h3">Tags Details</Header>}
									>
										{thisProject?.tagsID && (
											<EditGenericTags tagsId={thisProject.tagsID} />
										)}
									</Container>
								) : (
									<Container>
										{thisProject?.tagsID && (
											<EditGenericTagsEphemeral tagsId={thisProject.tagsID} />
										)}
									</Container>
								)}
							</RequiredPermissions>
						)}
					{thisProject.status === 'PENDING_APPROVAL' &&
						CURRENT_USER_ID !== thisProject.requestorID && (
							<RequiredPermissions
								requiredPermissions={CONSTANTS.PROJECT_APPROVER}
							>
								<Box float="right">
									<ButtonDropdown
										items={
											isAllTagsApproved
												? [
														{ text: 'To Approve...', id: 'to-approve' },
														{ text: 'To Reject...', id: 'to-reject' }
												  ]
												: [{ text: 'To Reject...', id: 'to-reject' }]
										}
										onItemClick={({ detail }) => {
											if (detail.id === 'to-approve') {
												setShowApprovalForm(true)
												setShowRejectionForm(false)
											}

											if (detail.id === 'to-reject') {
												setShowApprovalForm(false)
												setShowRejectionForm(true)
											}
										}}
										variant="primary"
									>
										Action
									</ButtonDropdown>
								</Box>
							</RequiredPermissions>
						)}

					{showApprovalForm &&
						(isEphemeral ? (
							<EphemeralApprovalForm
								thisProjectID={thisProjectID}
								shouldShowApprovalForm={setShowApprovalForm}
							/>
						) : (
							<ApprovalForm
								thisProjectID={thisProjectID}
								shouldShowApprovalForm={setShowApprovalForm}
							/>
						))}

					{showRejectionForm && (
						<RejectionForm
							thisProjectID={thisProjectID}
							shouldShowRejectionForm={setShowRejectionForm}
						/>
					)}
					{thisProject.status === 'APPROVED' && (
						<RequiredPermissions
							requiredPermissions={CONSTANTS.PROJECT_APPROVER}
            >
              <SpaceBetween direction='vertical' size='l'>
  							{isEphemeral ? (
  								<EphemeralApprovalDetails
  									approvalDetails={thisProject.approval!}
  								/>
  							) : (
  								<ApprovalDetails
  									approvalDetails={thisProject.approval!}
  									projectDetails={thisProject.details}
  								/>
                )}

                <AccessDetails
                  env={thisProject.details.environment.label}
                  platformName={thisProject.tags?.details.systemAlias}/>
              </SpaceBetween>

						</RequiredPermissions>
					)}
					{thisProject.status === 'REJECTED' && (
						<Box float="right">
							<Button
								variant="primary"
								onClick={() =>
									navigate(
										isEphemeral
											? `/projects/ephemeral/edit/${id}`
											: `/projects/edit/${id}`
									)
								}
							>
								Re-Open Request
							</Button>
						</Box>
					)}
				</SpaceBetween>

				<SpaceBetween direction="vertical" size="l">
					<Container header={<Header variant="h3">Comment History</Header>}>
						<CommentsOverview comments={thisProject.comments} />
					</Container>
					{thisProject.status === 'APPROVED' && isEphemeral === false && (
            <>
              <ProjectResourcePanel resources={projectResources} />
              <Container header={<Header variant='h3'>Application Resources</Header>}>
                <Button variant='link' onClick={() => {
                  const brokkrBaseUrl = process.env.REACT_APP_CODE_BROKKR_APP_URL;
                  let platformName = thisProject.tags?.details.systemAlias;
                  const searchParam = `?gl_subgroup=globetelecom/platforms/${platformName.toLowerCase()}/applications`;

                  window.open(`${brokkrBaseUrl}/builder${searchParam}`, '_blank');
                }}>
                  Provision Application Repository with Code Brokkr
                </Button>
              </Container>
						</>
					)}
				</SpaceBetween>
      </Grid>
		</ContentLayout>
	)
}

/** Normalize API value that may be string or { value, label } to a display string */
const toDisplayValue = (v: unknown): string => {
	if (v == null) return ''
	if (typeof v === 'object' && v !== null && 'label' in v) {
		const o = v as { label?: string; value?: string }
		return o.label ?? o.value ?? ''
	}
	if (typeof v === 'object' && v !== null && 'value' in v) {
		return String((v as { value: unknown }).value)
	}
	return String(v)
}

const normalizeWorkloadTypeDisplay = (value: unknown): string => {
	const raw = String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[_\s]+/g, '-')
	if (raw === 'eks') return 'EKS'
	if (raw === 'non-eks') return 'Non-EKS'
	if (raw.includes('non') && raw.includes('eks')) return 'Non-EKS'
	if (raw.includes('eks')) return 'EKS'
	return raw ? String(value).trim() : ''
}

const formatProjectPeopleList = (
	d: Record<string, any>,
	field: 'users' | 'approvers' = 'users'
): string => {
	let list: Array<{ displayName?: string; networkId?: string }> | undefined
	if (field === 'approvers') {
		list = d?.ssoListApprovers as Array<{ displayName?: string; networkId?: string }> | undefined
		if (!Array.isArray(list) || list.length === 0) {
			list = d?.projectApprovers as Array<{ displayName?: string; networkId?: string }> | undefined
		}
	} else {
		list = d?.ssoListUsers as Array<{ displayName?: string; networkId?: string }> | undefined
		if (!Array.isArray(list) || list.length === 0) {
			list = d?.projectUsers as Array<{ displayName?: string; networkId?: string }> | undefined
		}
		if (!Array.isArray(list) || list.length === 0) {
			const sso = d?.SSOUserNames as Array<{ displayName?: string; userName?: string }> | undefined
			if (Array.isArray(sso) && sso.length > 0) {
				list = sso.map((u) => ({
					displayName: String(u?.displayName ?? ''),
					networkId: String(u?.userName ?? '')
				}))
			}
		}
	}
	if (!Array.isArray(list) || list.length === 0) return ''
	return list
		.map((person) => {
			const name = String(person?.displayName ?? '').trim()
			const networkId = String(person?.networkId ?? '').trim()
			if (name && networkId) return `${name} (${networkId})`
			return name || networkId || ''
		})
		.filter(Boolean)
		.join(', ')
}

const getKeyValueProject = (project: Project) => {
	const d: any = project?.details || {}

	return [
		{
			label: 'Requester',
			value: project?.requestorID ?? '',
			version: getVersionStr(project?.version, 'requestorID')
		},
		{
			label: 'Project Name',
			value: toDisplayValue(d.projectName) || d.projectName,
			version: getVersionStr(project?.version, 'projectName')
		},
		{
			label: 'AWS Account ID',
			value: d.awsAccountID ?? '',
			version: getVersionStr(project?.version, 'awsAccountID')
		},
		{
			label: 'Distribution List',
			value: d.distributionList ?? '',
			version: getVersionStr(project?.version, 'distributionList')
		},
		{
			label: 'Workload Type',
			value: normalizeWorkloadTypeDisplay(d.accountWorkloadType),
			version: getVersionStr(project?.version, 'accountWorkloadType')
		},
		{
			label: 'SSO List Users',
			value: formatProjectPeopleList(d, 'users'),
			version: getVersionStr(project?.version, 'ssoListUsers')
		},
		{
			label: 'SSO list approvers',
			value: formatProjectPeopleList(d, 'approvers'),
			version: getVersionStr(project?.version, 'ssoListApprovers')
		},
		{
			label: 'Region',
			value: Array.isArray(d.region) ? (d.region.map((r: any) => r?.label ?? r?.value).filter(Boolean).join(', ') || '') : (d.region?.[0]?.label ?? ''),
			version: getVersionStr(project?.version, 'region')
		},

		// Project Details
		{
			label: 'Deployment',
			value: toDisplayValue(d.deployment) || toDisplayValue(d.projectDetails?.Deployment),
			version: getVersionStr(project?.version, 'deployment')
		},
		{
			label: 'Data Classification',
			value: toDisplayValue(d.dataClassification) || toDisplayValue(d.projectDetails?.['Data Classification']),
			version: getVersionStr(project?.version, 'dataClassification')
		},
		{
			label: 'Tier Level',
			value: toDisplayValue(d.tierLevel) || toDisplayValue(d.projectDetails?.['Tier Level']),
			version: getVersionStr(project?.version, 'tierLevel')
		},

		{
			label: 'Project Budget Allocated',
			value: d.budget ?? '',
			version: getVersionStr(project?.version, 'budget')
		},
		{
			label: 'CRFS Date',
			value: d.crfsDate ?? '',
			version: getVersionStr(project?.version, 'crfsDate')
		},
		{
			label: 'Maintenance Window Day (GMT +8)',
			value: toDisplayValue(d.maintenanceWindowDay),
			version: getVersionStr(project?.version, 'maintenanceWindowDay')
		},
		{
			label: 'Maintenance Window Time (GMT +8)',
			value: d.maintenanceWindowTime ?? '',
			version: getVersionStr(project?.version, 'maintenanceWindowTime')
		},
		{
			label: 'Environment',
			value: toDisplayValue(d.environment),
			version: getVersionStr(project?.version, 'environment')
		}
	]
}


const getKeyValueEphmeralProject = (project: Project) => {
	const d = project?.details
	return [
		{
			label: 'Requester',
			value: project?.requestorID ?? '',
			version: getVersionStr(project?.version, 'requestorID')
		},
		{
			label: 'Project Name',
			value: toDisplayValue(d?.project) || (d?.project?.value ?? ''),
			version: getVersionStr(project?.version, 'project')
		},
		{
			label: 'Workload',
			value: toDisplayValue(d?.workload) || (d?.workload?.value ?? ''),
			version: getVersionStr(project?.version, 'workload')
		},
		{
			label: 'AWS Account ID',
			value: d?.awsAccountID ?? '',
			version: getVersionStr(project?.version, 'awsAccountID')
		},
		{
			label: 'Environment',
			value: toDisplayValue(d?.environment) || (d?.environment?.label ?? ''),
			version: getVersionStr(project?.version, 'environment')
		},
		{
			label: 'Environment Type',
			value: toDisplayValue(d?.environment_type) || (d?.environment_type?.value ?? ''),
			version: getVersionStr(project?.version, 'environment_type')
		},
		{
			label: 'Teardown After Days',
			value: d?.teardown_after_days ?? '',
			version: getVersionStr(project?.version, 'teardown_after_days')
		}
	]
}

const getVersionStr = (version: Array<any> | undefined, key: string) => {
	let versionItems: string[] = []
	version?.forEach((element) => {
		const raw = element[key]
		if (raw === undefined || raw === null) return
		if (key === 'region' && element.region) {
			versionItems.unshift(`v${element.versionID}: ${toDisplayValue(element.region[0])}`)
		} else if (key === 'maintenanceWindowDay' && element.maintenanceWindowDay) {
			versionItems.unshift(`v${element.versionID}: ${toDisplayValue(element.maintenanceWindowDay)}`)
		} else {
			versionItems.unshift(`v${element.versionID}: ${toDisplayValue(raw)}`)
		}
	})
	return versionItems
}

export default ProjectDetails
